import { RestRequestBuilder } from "@frui.ts/apiclient";
import { IApiConnector } from "@frui.ts/apiclient/dist/types";
import { apiUrl, apiVersion } from "../config";
import AuthorizationService from "../services/authorizationService";
import LocaleProvider from "../services/localeProvider";
import ApiConnector from "./backendConnector";
import { interfaces } from "inversify";
import { plainToClass, ClassConstructor } from "class-transformer";
import { IResults } from "./types";

function appendAuthCodeHeader(params: RequestInit | undefined, authCode: string): RequestInit {
  return {
    ...params,
    headers: {
      ...(params || {}).headers,
      "Auth-Code": authCode,
    },
  };
}

const ContentTypes = {
  json: "application/json,text/json",
  pdf: "application/pdf",
};

function appendAcceptHeader(params: RequestInit | undefined, acceptContentType: string): RequestInit {
  return {
    ...params,
    headers: {
      ...(params || {}).headers,
      Accept: acceptContentType,
    },
  };
}

function appendContentTypeHeader(params: RequestInit | undefined, acceptContentType: string): RequestInit {
  return {
    ...params,
    headers: {
      ...(params || {}).headers,
      "Content-Type": acceptContentType,
    },
  };
}

function appendSignalRConnectionIdHeader(params: RequestInit | undefined, connectionId: string): RequestInit {
  return {
    ...params,
    headers: {
      ...(params || {}).headers,
      "SignalR-ConnectionId": connectionId,
    },
  };
}

function appendGTurnstileResponseHeader(params: RequestInit | undefined, token: string): RequestInit {
  return {
    ...params,
    headers: {
      ...(params || {}).headers,
      "cf-turnstile-response": token,
    },
  };
}

function appendISDSSessionIdHeader(params: RequestInit | undefined, sessionId: string): RequestInit {
  return {
    ...params,
    headers: {
      ...(params || {}).headers,
      "Isds-Session-Id": sessionId,
    },
  };
}

export type RequestBuilderConfiguration = {
  customApiUrl?: string;
  customApiVersion?: string;
  customApiConnector?: IApiConnector;
};

export default class EshopRequestBuilder extends RestRequestBuilder {
  protected configuration: RequestBuilderConfiguration | undefined;

  constructor(
    authorizationService: AuthorizationService,
    localeProvider: LocaleProvider,
    configuration?: RequestBuilderConfiguration
  ) {
    super(
      configuration?.customApiConnector || ApiConnector,
      `${configuration?.customApiUrl || apiUrl}${configuration?.customApiVersion || apiVersion}`,
      {
        headers: {
          ...authorizationService.buildHeaders(),
          "Accept-Language": localeProvider.locale === "en-GB" ? "en" : localeProvider.locale,
        },
      }
    );

    this.configuration = configuration;
  }

  apiVersion(version: number) {
    this.urlValue = `${this.configuration?.customApiUrl || apiUrl}/api/v${version}`;

    return this;
  }

  customApiUrlWithVersion(url: string, apiVersion: number) {
    const baseCustomUrl = url.split("/api/v")[0];
    this.urlValue = `${baseCustomUrl}/api/v${apiVersion}`;

    return this;
  }

  get<T>(
    queryParams?: any,
    authCode?: string,
    connectionId: string | undefined = undefined,
    sessionId: string | undefined = undefined,
    gTurnstileResponse: string | undefined | null = undefined
  ): Promise<T> {
    const requestUrl = this.appendQuery(this.urlValue, queryParams);
    let params = appendAcceptHeader(this.params, ContentTypes.json);

    if (authCode) {
      params = appendAuthCodeHeader(params, authCode);
    }

    if (connectionId) {
      params = appendSignalRConnectionIdHeader(params, connectionId);
    }

    if (gTurnstileResponse) {
      params = appendGTurnstileResponseHeader(params, gTurnstileResponse);
    }

    if (sessionId) {
      params = appendISDSSessionIdHeader(params, sessionId);
    }

    return this.apiConnector.get(requestUrl, params).then(x => x.json().catch(() => x));
  }

  getEntities<T>(returnType: ClassConstructor<T>, queryParams?: any): Promise<IResults<T>> {
    const requestUrl = this.appendQuery(this.url, queryParams);
    const params = appendAcceptHeader(this.params, ContentTypes.json);
    return this.apiConnector
      .get(requestUrl, params)
      .then(x => x.json())
      .then(x => {
        return {
          items: x.items.map((item: any) => plainToClass(returnType, item)),
        };
      });
  }

  clearGet(
    queryParams?: any,
    authCode?: string,
    connectionId: string | undefined = undefined,
    sessionId: string | undefined = undefined,
    gTurnstileResponse: string | undefined | null = undefined
  ): Promise<Response> {
    const requestUrl = this.appendQuery(this.urlValue, queryParams);
    let params = appendAcceptHeader(this.params, ContentTypes.json);

    if (authCode) {
      params = appendAuthCodeHeader(params, authCode);
    }

    if (connectionId) {
      params = appendSignalRConnectionIdHeader(params, connectionId);
    }

    if (gTurnstileResponse) {
      params = appendGTurnstileResponseHeader(params, gTurnstileResponse);
    }

    if (sessionId) {
      params = appendISDSSessionIdHeader(params, sessionId);
    }

    return this.apiConnector.get(requestUrl, params);
  }

  postData(data?: BodyInit, sessionId: string | undefined = undefined) {
    let params = appendAcceptHeader(this.params, ContentTypes.json);

    if (sessionId) {
      params = appendISDSSessionIdHeader(params, sessionId);
    }

    return this.apiConnector.post(this.urlValue, data, params);
  }

  post<T>(
    content: any,
    authCode?: string,
    connectionId: string | undefined = undefined,
    sessionId: string | undefined = undefined,
    gTurnstileResponse: string | undefined | null = undefined,
    contentTypes: string | undefined = undefined
  ): Promise<T> {
    let params = appendAcceptHeader(this.params, contentTypes ?? ContentTypes.json);

    if (authCode) {
      params = appendAuthCodeHeader(params, authCode);
    }

    if (connectionId) {
      params = appendSignalRConnectionIdHeader(params, connectionId);
    }

    if (gTurnstileResponse) {
      params = appendGTurnstileResponseHeader(params, gTurnstileResponse);
    }

    if (sessionId) {
      params = appendISDSSessionIdHeader(params, sessionId);
    }

    if (contentTypes) {
      params = appendAcceptHeader(this.params, ContentTypes.pdf);
      params = appendContentTypeHeader(params, ContentTypes.pdf);
    }

    return this.apiConnector.postJson(this.urlValue, content, params).then(x => x.json().catch(() => x));
  }

  clearPost(
    content: any,
    authCode?: string,
    connectionId: string | undefined = undefined,
    gTurnstileResponse: string | undefined | null = undefined,
    contentTypes: string | undefined = undefined
  ): Promise<Response> {
    let params = appendAcceptHeader(this.params, ContentTypes.json);

    if (authCode) {
      params = appendAuthCodeHeader(params, authCode);
    }

    if (connectionId) {
      params = appendSignalRConnectionIdHeader(params, connectionId);
    }

    if (gTurnstileResponse) {
      params = appendGTurnstileResponseHeader(params, gTurnstileResponse);
    }

    if (contentTypes) {
      params = appendAcceptHeader(this.params, ContentTypes.pdf);
      params = appendContentTypeHeader(params, ContentTypes.pdf);
    }

    return this.apiConnector.postJson(this.urlValue, content, params);
  }

  put<T>(content: any, authCode?: string, connectionId: string | undefined = undefined): Promise<T> {
    let params = appendAcceptHeader(this.params, ContentTypes.json);

    if (authCode) {
      params = appendAuthCodeHeader(params, authCode);
    }

    if (connectionId) {
      params = appendSignalRConnectionIdHeader(params, connectionId);
    }

    return this.apiConnector.putJson(this.urlValue, content, params).then(x => x.json().catch(() => undefined));
  }

  clearPut(
    content: any,
    authCode?: string,
    connectionId: string | undefined = undefined,
    gTurnstileResponse: string | undefined | null = undefined
  ): Promise<Response> {
    let params = appendAcceptHeader(this.params, ContentTypes.json);

    if (authCode) {
      params = appendAuthCodeHeader(params, authCode);
    }

    if (connectionId) {
      params = appendSignalRConnectionIdHeader(params, connectionId);
    }

    if (gTurnstileResponse) {
      params = appendGTurnstileResponseHeader(params, gTurnstileResponse);
    }

    return this.apiConnector.putJson(this.urlValue, content, params);
  }

  static Factory({ container }: interfaces.Context) {
    return (configuration?: RequestBuilderConfiguration) =>
      new EshopRequestBuilder(container.get(AuthorizationService), container.get(LocaleProvider), configuration);
  }
}
